import React, { useState } from "react"
import { Box, Tab, Tabs, useTheme } from "@mui/material"

export interface TabbedPageTab {
  id: string
  label: string
  content: React.ReactNode
}

interface TabbedPageContainerProps {
  tabs: TabbedPageTab[]
  defaultTabId?: string
  onTabChange?: (id: string) => void
}

export const TabbedPageContainer = ({
  tabs,
  defaultTabId,
  onTabChange
}: TabbedPageContainerProps) => {
  const { palette } = useTheme()
  const activeId = defaultTabId && tabs.some((t) => t.id === defaultTabId) ? defaultTabId : tabs[0]?.id ?? ""
  const [selectedId, setSelectedId] = useState(activeId)
  const currentId = selectedId && tabs.some((t) => t.id === selectedId) ? selectedId : tabs[0]?.id ?? ""

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedId(newValue)
    onTabChange?.(newValue)
  }

  const activeTabBg = palette.primary.main
  const inactiveTabBg = palette.primary.light

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Tabs
        value={currentId}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexShrink: 0,
          backgroundColor: palette.gray[50],
          "& .MuiTabs-flexContainer": {
            px: 8,
            pt: 4,
            gap: 0
          },
          "& .MuiTabs-indicator": {
            display: "none"
          },
          "& .MuiTabs-scroller": {
            borderBottom: `3px solid ${palette.primary.main}`
          }
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === currentId
          return (
            <Tab
              key={tab.id}
              label={tab.label}
              value={tab.id}
              sx={{
                minHeight: 48,
                width: "235px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "18px",
                lineHeight: "21px",
                borderRadius: "25px 25px 0px 0px;",
                backgroundColor: isActive ? activeTabBg : inactiveTabBg,
                boxShadow: isActive ? "-5px -5px 10px #0000001A" : "0px -2px 10px #0000001A",
                color: palette.text.primary,
                ml: -2,
                position: "relative",
                zIndex: isActive ? 2 : 1,
                "&:first-of-type": {
                  ml: 0
                },
                "&.Mui-selected": {
                  color: palette.primary.contrastText,
                  backgroundColor: activeTabBg
                },
                "&:hover": {
                  backgroundColor: isActive ? activeTabBg : (palette.primary as any)[100],
                  zIndex: 3
                }
              }}
            />
          )
        })}
      </Tabs>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          display: "flex",
          flexDirection: "column"
        }}
      >
        {tabs.map((tab) => (tab.id === currentId ? <React.Fragment key={tab.id}>{tab.content}</React.Fragment> : null))}
      </Box>
    </Box>
  )
}

export default TabbedPageContainer
